/**
 * @file     Notice.tsx
 * @purpose  Hub notice: a soft status band at radius 20 in the info, warning
 *           or error tint, with a tone glyph and either one line or a title
 *           and one line. Text stays in the primary and secondary ink so it
 *           reads at 4.5:1 on every tint; the tone lives in the glyph and the
 *           wash. Errors announce as alerts, the rest as status.
 */
import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import { layoutTokens } from '@/design-system/tokens/layout';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { hubColors } from './hubKitStyles';

export type NoticeTone = 'info' | 'warning' | 'error';

export interface NoticeProps {
  tone?: NoticeTone;
  title?: string;
  /** The one line. */
  children: ReactNode;
  /** One trailing control, e.g. a retry link. */
  action?: ReactNode;
}

const toneInk: Record<NoticeTone, string> = {
  info: hubColors.statusInfo,
  warning: hubColors.statusWarning,
  error: hubColors.statusError,
};

const Glyph = ({ tone }: { tone: NoticeTone }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {tone === 'warning' ? (
      <>
        <path d="M12 4l9 16H3z" />
        <path d="M12 10v4M12 17.2v.3" />
      </>
    ) : (
      <>
        <circle cx="12" cy="12" r="9" />
        {tone === 'error' ? <path d="M12 7.5v5M12 16v.3" /> : <path d="M12 11v5.5M12 7.8v.3" />}
      </>
    )}
  </svg>
);

export function Notice({ tone = 'info', title, children, action }: NoticeProps) {
  const ink = toneInk[tone];
  return (
    <Box
      role={tone === 'error' ? 'alert' : 'status'}
      data-notice={tone}
      sx={{
        display: 'flex',
        alignItems: title ? 'flex-start' : 'center',
        gap: 3,
        px: { xs: 4, sm: 5 },
        py: { xs: 3.5, sm: 4 },
        borderRadius: `${layoutTokens.radius.lg}px`,
        backgroundColor: `color-mix(in srgb, ${ink} 12%, transparent)`,
        color: hubColors.textPrimary,
      }}
    >
      <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0, color: ink, mt: title ? '1px' : 0 }}>
        <Glyph tone={tone} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {title ? (
          <Box component="p" sx={{ m: 0, fontSize: 15, fontWeight: fontWeightTokens.semibold, lineHeight: 1.4 }}>
            {title}
          </Box>
        ) : null}
        <Box
          component="div"
          sx={{ m: 0, fontSize: 14.5, lineHeight: 1.55, color: title ? hubColors.textSecondary : hubColors.textPrimary }}
        >
          {children}
        </Box>
      </Box>
      {action ? <Box sx={{ flexShrink: 0, alignSelf: 'center' }}>{action}</Box> : null}
    </Box>
  );
}
